import { useCallback } from 'react';
import { codePrefixesService } from '../services';
import {
  useResourceCollection,
  useResourceById,
  useCreateResource,
  useUpdateResource,
  useDeleteResource,
  ResourceState,
  SingleResourceState,
  MutationState,
} from './useResource';
import { ResourceQueryOptions } from '../services/resources.service';
import { CodePrefix } from '../types';

export function useCodePrefixes(
  options?: ResourceQueryOptions
): ResourceState<CodePrefix> & { meta?: any } {
  const fetchFn = useCallback(
    (opts?: ResourceQueryOptions) =>
      codePrefixesService.getAll(opts) as unknown as Promise<{ data: CodePrefix[]; meta?: any }>,
    []
  );
  return useResourceCollection<CodePrefix>(fetchFn, options);
}

export function useCodePrefix(
  id: string | null,
  options?: ResourceQueryOptions
): SingleResourceState<CodePrefix> {
  const numericId = id ? parseInt(id, 10) : null;
  const fetchFn = useCallback(
    (prefixId: number, opts?: ResourceQueryOptions) =>
      codePrefixesService.getById(prefixId, opts) as unknown as Promise<CodePrefix>,
    []
  );
  return useResourceById<CodePrefix>(fetchFn, numericId, options);
}

export function useCreateCodePrefix(
  onSuccess?: (data: CodePrefix) => void
): MutationState<CodePrefix> {
  const createFn = useCallback(
    (data: any) => codePrefixesService.create(data) as unknown as Promise<CodePrefix>,
    []
  );
  return useCreateResource<CodePrefix>(createFn, onSuccess);
}

export function useUpdateCodePrefix(
  onSuccess?: (data: CodePrefix) => void
): MutationState<CodePrefix> {
  const updateFn = useCallback(
    (id: string | number, data: any) => {
      const numericId = typeof id === 'string' ? parseInt(id, 10) : id;
      return codePrefixesService.update(numericId, data) as unknown as Promise<CodePrefix>;
    },
    []
  );
  return useUpdateResource<CodePrefix>(updateFn, onSuccess);
}

/**
 * Delete code prefix using documentId
 * @param data - Full code prefix object containing documentId
 */
export function useDeleteCodePrefix(onSuccess?: () => void): MutationState<void> {
  const deleteFn = useCallback(
    (data: any) => {
      const documentId = data.documentId || data.id?.toString() || data.id;
      return codePrefixesService.delete(documentId) as unknown as Promise<void>;
    },
    []
  );
  return useDeleteResource(deleteFn, onSuccess);
}

